import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import ApplicationProgress from '../../components/ui/ApplicationProgress';
import PersonalInfoStep from './components/PersonalInfoStep';
import EducationStep from './components/EducationStep';
import ProgramSelectionStep from './components/ProgramSelectionStep';
import DocumentUploadStep from './components/DocumentUploadStep';
import ReviewStep from './components/ReviewStep';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const ApplicationForm = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    nationality: '',
    passportNumber: '',
    email: '',
    phone: '',
    streetAddress: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    highestEducation: '',
    institutionName: '',
    fieldOfStudy: '',
    gpa: '',
    graduationDate: '',
    yearsOfStudy: '',
    chineseLevel: '',
    hskScore: '',
    hskTestDate: '',
    yearsOfChinese: '',
    educationHistory: [],
    institution: '',
    programType: '',
    duration: '',
    intake: '',
    accommodation: '',
    airportPickup: false,
    culturalActivities: false,
    languageExchange: false,
    partTimeWork: false,
    preferredCurrency: 'usd',
    specialRequirements: '',
    documents: {},
    declarationAccurate: false,
    termsAccepted: false,
    dataConsent: false,
    emailConsent: false
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalSteps = 5;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData?.firstName?.trim()) newErrors.firstName = 'First name is required';
      if (!formData?.lastName?.trim()) newErrors.lastName = 'Last name is required';
      if (!formData?.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
      if (!formData?.gender) newErrors.gender = 'Gender is required';
      if (!formData?.nationality) newErrors.nationality = 'Nationality is required';
      if (!formData?.passportNumber?.trim()) newErrors.passportNumber = 'Passport number is required';
      if (!formData?.email?.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
        newErrors.email = 'Invalid email format';
      }
      if (!formData?.phone?.trim()) newErrors.phone = 'Phone number is required';
      if (!formData?.streetAddress?.trim()) newErrors.streetAddress = 'Street address is required';
      if (!formData?.city?.trim()) newErrors.city = 'City is required';
      if (!formData?.state?.trim()) newErrors.state = 'State/Province is required';
      if (!formData?.postalCode?.trim()) newErrors.postalCode = 'Postal code is required';
      if (!formData?.country) newErrors.country = 'Country is required';
    }

    if (step === 2) {
      if (!formData?.highestEducation) newErrors.highestEducation = 'Education level is required';
      if (!formData?.institutionName?.trim()) newErrors.institutionName = 'Institution name is required';
      if (!formData?.fieldOfStudy) newErrors.fieldOfStudy = 'Field of study is required';
      if (!formData?.graduationDate) newErrors.graduationDate = 'Graduation date is required';
      if (!formData?.chineseLevel) newErrors.chineseLevel = 'Chinese level is required';
    }

    if (step === 3) {
      if (!formData?.institution) newErrors.institution = 'Institution selection is required';
      if (!formData?.programType) newErrors.programType = 'Program type is required';
      if (!formData?.duration) newErrors.duration = 'Study duration is required';
      if (!formData?.intake) newErrors.intake = 'Intake period is required';
      if (!formData?.accommodation) newErrors.accommodation = 'Accommodation preference is required';
    }

    if (step === 4) {
      const requiredDocs = ['passport', 'photo', 'diploma', 'transcript'];
      const documentErrors = {};
      requiredDocs?.forEach(docId => {
        if (!formData?.documents?.[docId]) {
          documentErrors[docId] = 'This document is required';
        }
      });
      if (Object.keys(documentErrors)?.length > 0) {
        newErrors.documents = documentErrors;
      }
    }

    if (step === 5) {
      if (!formData?.declarationAccurate) newErrors.declarationAccurate = 'You must confirm the accuracy of information';
      if (!formData?.termsAccepted) newErrors.termsAccepted = 'You must accept the terms and conditions';
      if (!formData?.dataConsent) newErrors.dataConsent = 'Data processing consent is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep(prev => prev + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleEdit = (step) => {
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) {
      return;
    }

    setIsSubmitting(true);

    setTimeout(() => {
      console.log('Application submitted:', formData);
      alert('Application submitted successfully! You will receive a confirmation email shortly.');
      navigate('/user-dashboard');
    }, 2000);
  };

  const handleSaveDraft = () => {
    console.log('Draft saved:', formData);
    alert('Application draft saved successfully!');
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <PersonalInfoStep
            formData={formData}
            errors={errors}
            onChange={handleChange}
          />
        );
      case 2:
        return (
          <EducationStep
            formData={formData}
            errors={errors}
            onChange={handleChange}
          />
        );
      case 3:
        return (
          <ProgramSelectionStep
            formData={formData}
            errors={errors}
            onChange={handleChange}
          />
        );
      case 4:
        return (
          <DocumentUploadStep
            formData={formData}
            errors={errors}
            onChange={handleChange}
          />
        );
      case 5:
        return (
          <ReviewStep
            formData={formData}
            errors={errors}
            onChange={handleChange}
            onEdit={handleEdit}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="pt-20">
        <Breadcrumbs />
      </div>

      <main className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-4 xl:col-span-3">
            <div className="lg:sticky lg:top-24">
              <ApplicationProgress currentStep={currentStep} totalSteps={totalSteps} />

              <div className="mt-6 p-4 md:p-6 bg-card rounded-lg shadow-warm-sm border border-border">
                <div className="flex items-start gap-3">
                  <Icon name="HelpCircle" size={20} color="var(--color-primary)" />
                  <div>
                    <p className="text-sm md:text-base font-caption font-medium text-foreground mb-2">
                      Need Help?
                    </p>
                    <p className="text-xs md:text-sm text-muted-foreground font-caption mb-3">
                      Our support team is here to assist you with your application.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="MessageCircle"
                      iconPosition="left"
                      fullWidth
                    >
                      Contact Support
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8 xl:col-span-9">
            <div className="bg-card rounded-lg shadow-warm p-4 md:p-6 lg:p-8 border border-border">
              {renderStepContent()}

              <div className="mt-8 pt-6 border-t border-border">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-3 w-full sm:w-auto">
                    {currentStep > 1 && (
                      <Button
                        variant="outline"
                        iconName="ChevronLeft"
                        iconPosition="left"
                        onClick={handlePrevious}
                        className="flex-1 sm:flex-initial"
                      >
                        Previous
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      iconName="Save"
                      iconPosition="left"
                      onClick={handleSaveDraft}
                      className="flex-1 sm:flex-initial"
                    >
                      Save Draft
                    </Button>
                  </div>

                  <div className="w-full sm:w-auto">
                    {currentStep < totalSteps ? (
                      <Button
                        variant="default"
                        iconName="ChevronRight"
                        iconPosition="right"
                        onClick={handleNext}
                        fullWidth
                      >
                        Next Step
                      </Button>
                    ) : (
                      <Button
                        variant="default"
                        iconName="Send"
                        iconPosition="left"
                        onClick={handleSubmit}
                        loading={isSubmitting}
                        disabled={isSubmitting}
                        fullWidth
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit Application'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 md:p-6 bg-muted/30 rounded-lg border border-border">
              <div className="flex items-start gap-3">
                <Icon name="Shield" size={20} color="var(--color-primary)" />
                <div>
                  <p className="text-sm md:text-base font-caption font-medium text-foreground mb-1">
                    Your Data is Secure
                  </p>
                  <p className="text-xs md:text-sm text-muted-foreground font-caption">
                    All information is encrypted and stored securely. We comply with international data protection regulations including GDPR and CCPA.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ApplicationForm;